<template>
  <div class="relative flex flex-col">
    <command-input
      ref="searchInputRef"
      :placeholder="placeholder || $t('COMMON.TEXT')"
      :type="AiDialogType.AgentChat"
      :file-uploading-disabled="isSendingPrompt"
      class="chat mr-0! ml-0! w-full"
      :auto-focus
      is-chat
      @command:update="chatSession.message = $event"
      @command:submit="handleEnter"
    >
      <template #actions>
        <div
          class="absolute top-1/2 right-3 mr-2 flex -translate-y-1/2 items-center p-2"
        >
          <button
            aria-label="Send message"
            class="rounded px-2 py-1 text-disabled-medium hover:bg-primary hover:text-white disabled:opacity-50 disabled:hover:bg-disabled disabled:hover:text-disabled-medium"
            :class="{ 'bg-primary text-white': canSend }"
            :disabled="!canSend"
            data-cy="chat-send-button"
            @click="handleSend"
          >
            <icon-base
              :icon="sendButtonIconInfo.icon"
              :class="sendButtonIconInfo.class"
              :view-box="sendButtonIconInfo.viewBox"
            />
          </button>
        </div>
        <div
          v-if="chatSession.type"
          class="absolute -top-[250px] left-0 flex h-[240px] w-9/10 overflow-auto rounded-lg border border-divider bg-white shadow-[0_5px_20px_0_rgba(0,0,0,0.08)]"
        >
          <ai-results
            :type="chatSession.type"
            is-chat
            @agent:selected="handleAgentSelected"
            @deal:selected="handleDealSelected"
          />
        </div>
      </template>
    </command-input>
  </div>
</template>

<script setup lang="ts">
import { watchDebounced } from "@vueuse/core";
import { computed, ref, useTemplateRef } from "vue";

import { storeToRefs } from "pinia";

import { AiDialogType } from "@/enums/ai";
import {
  AGENT_SEARCH_PREFIX,
  PROMPT_DEAL_PREFIX,
  getSelectedDealPath,
} from "@/helpers/agents";
import { VALID_URL_REGEX } from "@/helpers/constants";
import { NON_BREAKING_SPACE } from "@/helpers/constants/aiAgents";
import { useSystem } from "@/hooks/common";
import { useAiAgentsStore } from "@/stores/ai/agents";
import { useFiles } from "@/stores/ai/files";
import { useAiSearchStore } from "@/stores/aiSearch";

import AiResults from "@/components/cmdK/AiResults.vue";
import CommandInput from "@/components/cmdK/CommandInput.vue";
import IconLoadingCircle from "@/components/icons/IconLoadingCircle.vue";
import IconSend from "@/components/icons/IconSend.vue";
import IconBase from "@/components/ui/IconBase.vue";

import type { AiAgentBase } from "@/models/aiAgents";
import type { AiSearchDeal } from "@/models/cmdK";

const {
  disabled = false,
  placeholder = "",
  autoFocus = true,
} = defineProps<{
  disabled?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
}>();

const emit = defineEmits<{ send: [message: string] }>();

const { systemMetaKey } = useSystem();

const aiSearchStore = useAiSearchStore();
const aiAgentsStore = useAiAgentsStore();
const aiFilesStore = useFiles();

const { selectedFiles } = storeToRefs(aiFilesStore);

const searchInputRef = useTemplateRef("searchInputRef");

const { agentSessionInfo } = storeToRefs(aiAgentsStore);
const { dealResults } = storeToRefs(aiSearchStore);

const chatSession = ref<{
  message: string;
  type: AiDialogType | null;
  deals: AiSearchDeal[];
  agents: AiAgentBase[];
}>({
  message: "",
  type: null,
  deals: [],
  agents: [],
});

const isSendingPrompt = computed(() => agentSessionInfo.value.isSendingPrompt);

const canSend = computed(
  () =>
    (!!chatSession.value.message.trim().length || selectedFiles.value.length) &&
    !isSendingPrompt.value &&
    !disabled
);

const sendButtonIconInfo = computed(() => {
  if (isSendingPrompt.value) {
    return {
      icon: IconLoadingCircle,
      class: "animate-spin",
      viewBox: "0 0 20 20",
    };
  }
  return {
    icon: IconSend,
    class: "",
    viewBox: "0 0 16 16",
  };
});

const handleEnter = () => {
  if (!systemMetaKey.value || !chatSession.value.message.trim().length) {
    return;
  }
  handleSend();
};

const handleSend = () => {
  if (!canSend.value) {
    return;
  }

  emit("send", chatSession.value.message);
  chatSession.value.message = "";
  agentSessionInfo.value.htmlSearchContent = "";
  searchInputRef.value?.deleteAllContents();
};

const handleAgentSelected = (agent: AiAgentBase) => {
  searchInputRef.value?.insertItem("agent", agent.name, agent.id);

  chatSession.value.type = null;
};

const handleDealSelected = () => {
  const deal = dealResults.value?.find(
    (deal) => deal.id === agentSessionInfo.value.deal
  );

  if (!deal) {
    return;
  }

  searchInputRef.value?.insertItem(
    "deal",
    deal.application_name,
    getSelectedDealPath(deal.id)
  );

  chatSession.value.type = null;
};

watchDebounced(
  () => chatSession.value.message,
  async (updatedValue) => {
    const searchPhrase = updatedValue.replace(
      new RegExp(VALID_URL_REGEX.source, "g"),
      ""
    );

    const mentionPrefix = searchPhrase.includes(PROMPT_DEAL_PREFIX)
      ? PROMPT_DEAL_PREFIX
      : AGENT_SEARCH_PREFIX;

    if (!searchPhrase.includes(mentionPrefix)) {
      chatSession.value.type = null;
      return;
    }

    const isDealPrompt = searchPhrase.includes(PROMPT_DEAL_PREFIX);

    const searchPhraseWithoutNonBreakingSpace = searchPhrase
      .replace(new RegExp(NON_BREAKING_SPACE, "g"), " ")
      .trim();

    const searchPrompt = searchPhraseWithoutNonBreakingSpace
      .substring(
        searchPhraseWithoutNonBreakingSpace.lastIndexOf(mentionPrefix) + 1
      )
      .trim();

    if (!searchPrompt) {
      return;
    }

    if (isDealPrompt) {
      chatSession.value.type = AiDialogType.Deals;
      const response = await aiSearchStore.aiSearchDeals(
        searchPrompt,
        undefined,
        agentSessionInfo.value.abortControllers.searchDeals.signal
      );

      chatSession.value.deals = response.data;
      return;
    }

    /**
     * Once BE implements the Agent-in-Agent logic,
     * uncomment this and check on the agent insertion.
     */

    // const agentsResponse = await aiAgentsStore.getAll({
    //   search: searchPrompt,
    // });

    // chatSession.value.agents = agentsResponse;
    // chatSession.value.type = AiDialogType.Agents;
  },
  { debounce: 600 }
);
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
  overflow-y: auto;
}

.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
</style>
